/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Grid, Typography, Typography2, breakpoints, useTheme } from '@coursera/cds-core';

import type {
  EntWebsiteBlockTestimonialQuery,
  EntWebsiteTestimonialFragment,
} from 'bundles/ent-website/__generated__/contentfulTypes';
// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import BlockLayout, { topImageOnPhone } from 'bundles/ent-website/contentful/core/BlockLayout';
import Image from 'bundles/ent-website/contentful/core/Image';
import Portrait from 'bundles/ent-website/contentful/core/Portrait';
import { entWebsiteBlockTestimonialQuery } from 'bundles/ent-website/queries/EntWebsiteTestimonial';
import parseLayoutOptions from 'bundles/ent-website/utils/parseLayoutOptions';
import type { BlockModuleInnerProps } from 'bundles/ent-website/utils/withBlockQuery';
import withBlockQuery from 'bundles/ent-website/utils/withBlockQuery';

type Props = BlockModuleInnerProps<EntWebsiteTestimonialFragment> & {
  isCarouselSlide?: boolean;
};

export function TestimonialModule({ content, assembly, isCarouselSlide = false }: Props) {
  const { headline, testimonial, author, authorAvatar, authorOrganization, authorTitle } = content ?? {};
  const { visibility } = assembly ?? {};
  const isFullWidth = assembly.layout === 'no-image';
  const theme = useTheme();
  const options = parseLayoutOptions(assembly.layoutOptions);

  const stylesFull = {
    portraitPanel: css`
      width: 100%;
      margin-inline-end: var(--cds-spacing-800);

      ${breakpoints.down('xs')} {
        margin-inline-end: 0;
        margin-bottom: var(--cds-spacing-400);
        display: flex;
        align-items: flex-start;
        justify-content: ${isCarouselSlide ? 'center' : 'flex-start'};
      }
    `,

    mainWrapper: isFullWidth
      ? css`
          max-width: 772px;
          margin: 0 auto;
        `
      : css`
          max-width: 575px;
        `,

    headline: css`
      margin-bottom: var(--cds-spacing-400);
      ${breakpoints.down('xs')} {
        line-height: 40px !important;
      }
    `,
    portrait: css`
      width: 200px;
      height: 200px;

      ${breakpoints.down('xs')} {
        width: 160px;
        height: 160px;
        margin: 0;
      }
    `,

    testimonial: css`
      font-size: 24px !important;
      line-height: 36px !important;
      font-weight: 400 !important;
      position: relative;

      &::before {
        position: absolute;
        left: -64px;
        top: -16px;
        font-size: 80px;
        line-height: 80px;
        font-weight: 600;
        content: '\u201c';
      }

      ${breakpoints.down('sm')} {
        &::before {
          font-size: 48px;
          line-height: 48px;
          left: -32px;
          top: -12px;
        }
      }

      ${breakpoints.down('xs')} {
        font-size: 16px !important;
        line-height: 24px !important;

        &::before {
          position: static;
          left: 0;
          top: 0;
          font-size: inherit;
          line-height: inherit;
          font-weight: inherit;
        }
      }
    `,

    authorSection: css`
      margin-top: var(--cds-spacing-800);
      display: flex;
      flex-direction: row;
      flex-wrap: nowrap;

      ${breakpoints.down('xs')} {
        margin-top: var(--cds-spacing-400);
        margin-bottom: ${isCarouselSlide ? '0' : 'var(--cds-spacing-600)'};
      }
    `,

    authorName: css`
      font-size: 20px !important;
      font-weight: 600 !important;
      line-height: 150% !important;
      letter-spacing: -0.03em;
      margin-bottom: var(--cds-spacing-100);
    `,

    authorTitle: css`
      font-size: 16px !important;
      line-height: 150% !important;
    `,

    authorOrgName: css`
      font-size: 16px !important;
      line-height: 21px !important;
    `,

    authorOrgLogo: css`
      margin-inline-end: var(--cds-spacing-400);
      font-size: 14px !important;
      line-height: 21px !important;

      ${breakpoints.down('xs')} {
        max-height: 64px;
        margin-bottom: var(--cds-spacing-200);
      }
    `,

    authorAvatar: css`
      max-width: 100%;
      max-height: 410px;
      ${breakpoints.down('sm')} {
        max-height: 280px;
      }

      ${breakpoints.down('xs')} {
        max-height: 240px;
      }
    `,
  };

  const stylesEmbedded = {
    main: css`
      border-left: 3px solid var(--cds-color-grey-50);
      padding-left: var(--cds-spacing-300);

      ${breakpoints.down('xs')} {
        margin-bottom: var(--cds-spacing-600);
      }
    `,

    headline: css`
      margin-bottom: var(--cds-spacing-100);
      font-size: 20px !important;
      line-height: 150% !important;

      ${breakpoints.down('xs')} {
        font-size: 16px !important;
      }
    `,

    testimonial: css`
      font-size: 24px;
      font-style: normal;
      font-weight: 400;
      line-height: 32px; /* 133.333% */
      letter-spacing: -0.1px;
      margin-bottom: var(--cds-spacing-400);

      &::before {
        content: '\u201c';
      }

      &::after {
        content: '\u201d';
      }
    `,

    authorSection: css`
      margin-top: var(--cds-spacing-400);
      display: flex;
      flex-direction: row;
      align-items: center;
      flex-wrap: nowrap;
    `,
  };

  const styles = { ...stylesFull, ...(options.innerLayout ? stylesEmbedded : {}) };

  const isVertical = assembly.layout === 'image-top/text-bottom';

  const imagePanel = () =>
    authorAvatar && visibility.authorAvatar && !options.innerLayout ? (
      <div css={styles.portraitPanel}>
        <Portrait
          portrait={authorAvatar}
          css={[styles.portrait, isVertical ? { margin: `0 0 var(--cds-spacing-400)` } : {}]}
        />
      </div>
    ) : null;

  const contentPanel = () => (
    <div css={styles.mainWrapper}>
      {visibility.headline && headline && (
        <Typography variant="d2semibold" css={styles.headline}>
          {headline}
        </Typography>
      )}
      {visibility.testimonial && testimonial && (
        <Typography2 component="p" variant="bodyPrimary" css={styles.testimonial}>
          {testimonial}
        </Typography2>
      )}
      <Grid container css={styles.authorSection}>
        {visibility.authorOrganization && authorOrganization?.logo && (
          <Grid item css={styles.authorOrgLogo}>
            <Image
              content={authorOrganization.logo}
              width={authorOrganization.logo.width ?? 'auto'}
              css={{
                maxWidth: 100,
                maxHeight: 100,
                width: '100px',
                objectFit: 'contain',
              }}
            />
          </Grid>
        )}
        <Grid item>
          {visibility.author && author && (
            <Typography2 variant="bodyPrimary" component="div" css={styles.authorName}>
              {author}
            </Typography2>
          )}
          {visibility.authorTitle && authorTitle && (
            <Typography2 variant="bodyPrimary" component="div" css={styles.authorTitle}>
              {authorTitle}
            </Typography2>
          )}
          {visibility.authorTitle &&
            visibility.authorOrganization &&
            authorOrganization?.name &&
            !options.innerLayout && (
              <Typography2 variant="bodyPrimary" component="div" css={styles.authorOrgName}>
                {authorOrganization.name}
              </Typography2>
            )}
        </Grid>
      </Grid>
    </div>
  );

  return <BlockLayout assembly={topImageOnPhone(assembly)} renderContent={contentPanel} renderImage={imagePanel} />;
}

// @ts-expect-error TSMIGRATION-5.0 TS2344
export const BlockTestimonialModule = withBlockQuery<EntWebsiteTestimonialFragment, EntWebsiteBlockTestimonialQuery>(
  entWebsiteBlockTestimonialQuery
)(TestimonialModule);
